import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';

const LESSON_SAMPLE_SIZE = 50;

/**
 * Compute learned signals from recent lesson history.
 *
 * Reads the last LESSON_SAMPLE_SIZE lessonInstances for the instructor
 * and derives:
 *   - preferredDrills: top drill types by frequency
 *   - lessonStructure: most common ordered stage sequence
 *
 * Returns null if no lesson data is available.
 *
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} instructorId
 * @param {string} orgId
 * @returns {Promise<{ preferredDrills: string[], lessonStructure: string[] }|null>}
 */
export async function computeLearnedSignals(db, instructorId, orgId) {
  if (!db || !instructorId || !orgId) return null;

  try {
    const lessonsRef = collection(db, 'orgs', orgId, 'lessonInstances');
    const q = query(
      lessonsRef,
      where('instructorId', '==', instructorId),
      orderBy('scheduledAt', 'desc'),
      limit(LESSON_SAMPLE_SIZE)
    );
    const snap = await getDocs(q);

    if (snap.empty) return null;

    const docs = snap.docs.map(d => d.data());

    const preferredDrills = computePreferredDrills(docs);
    const lessonStructure = computeLessonStructure(docs);

    return { preferredDrills, lessonStructure };
  } catch (_) {
    return null;
  }
}

/**
 * Count drill type occurrences across lesson docs and return the top 5
 * drill names by frequency.
 *
 * Expects each lesson doc to have an optional `drills` array, where each
 * element has a `type` or `name` string field.
 */
function computePreferredDrills(docs) {
  const counts = {};

  for (const doc of docs) {
    const drills = doc.drills;
    if (!Array.isArray(drills)) continue;
    for (const drill of drills) {
      const key = drill.type || drill.name;
      if (typeof key === 'string' && key.trim()) {
        counts[key] = (counts[key] || 0) + 1;
      }
    }
  }

  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name]) => name);
}

/**
 * Extract lesson stage sequences from each lesson doc and return the
 * most common sequence.
 *
 * Expects each lesson doc to have an optional `stages` array, where each
 * element has a `label` string field.
 */
function computeLessonStructure(docs) {
  const sequenceCounts = {};

  for (const doc of docs) {
    const stages = doc.stages;
    if (!Array.isArray(stages) || stages.length === 0) continue;
    const seq = stages
      .map(s => s.label || s.type || s.name)
      .filter(Boolean)
      .join('||');
    if (seq) {
      sequenceCounts[seq] = (sequenceCounts[seq] || 0) + 1;
    }
  }

  if (Object.keys(sequenceCounts).length === 0) return [];

  const topSeq = Object.entries(sequenceCounts).sort((a, b) => b[1] - a[1])[0][0];
  return topSeq.split('||');
}
